'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { z, type Infer } from '@/lib/validation/zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useId, useMemo, useState, type MouseEvent } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useLegalDialog } from '@/components/integrations/legal/legal-dialog-provider'
import { FormErrorBanner } from '@/components/site/forms/form-error-banner'
import { formatMessage } from '@/components/site/forms/form-helpers'
import { ReCaptcha } from '@/components/site/forms/register/recaptcha'
import { Button } from '@/components/ui/button'
import { Checkbox, CheckboxField } from '@/components/ui/checkbox'
import { ErrorMessage, Field, Label } from '@/components/ui/fieldset'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { analytics } from '@/lib/analytics/posthog'

import { recaptchaSiteKey } from '@/config/env.public'
import type { Dictionary } from '@/i18n/dictionaries/base'
import { identifyByEmail, phoneForPerson, splitFullName } from '@/lib/analytics/identity'
import { firePixelConversion } from '@/lib/analytics/pixels'

type ContactFormCopy = Dictionary['pages']['contact']['form']
type ContactFormFields = Dictionary['ui']['fields']
type ContactFormPlaceholders = Dictionary['ui']['placeholders']['contact']
type ContactFormActions = Dictionary['ui']['actions']
type ContactFormLegalLabels = Dictionary['ui']['labels']['pages']
type ContactFormValidation = Dictionary['ui']['validation']

type ContactFormProps = {
  copy: ContactFormCopy
  fields: ContactFormFields
  placeholders: ContactFormPlaceholders
  actions: ContactFormActions
  legalLabels: ContactFormLegalLabels
  validation: ContactFormValidation
  submitEndpoint?: string
  locale: string
}

const LIMITS = {
  company: { min: 2, max: 128 },
  name: { min: 2, max: 128 },
  email: { min: 2, max: 256 },
  phone: { max: 128 },
  message: { min: 20, max: 1024 },
} as const

const createSchema = (validation: ContactFormValidation) => {
  return z
    .object({
      company: z
        .string()
        .trim()
        .min(1, validation.companyRequired)
        .min(LIMITS.company.min, formatMessage(validation.companyMin, { min: LIMITS.company.min }))
        .max(LIMITS.company.max, formatMessage(validation.companyMax, { max: LIMITS.company.max })),
      name: z
        .string()
        .trim()
        .min(1, validation.nameRequired)
        .min(LIMITS.name.min, formatMessage(validation.nameMin, { min: LIMITS.name.min }))
        .max(LIMITS.name.max, formatMessage(validation.nameMax, { max: LIMITS.name.max })),
      email: z
        .string()
        .trim()
        .min(1, validation.emailRequired)
        .min(LIMITS.email.min, formatMessage(validation.emailMin, { min: LIMITS.email.min }))
        .max(LIMITS.email.max, formatMessage(validation.emailMax, { max: LIMITS.email.max }))
        .email(validation.emailInvalid),
      phone: z
        .string()
        .trim()
        .max(LIMITS.phone.max, formatMessage(validation.phoneMax, { max: LIMITS.phone.max })),
      message: z
        .string()
        .trim()
        .min(1, validation.messageRequired)
        .min(LIMITS.message.min, formatMessage(validation.messageMin, { min: LIMITS.message.min }))
        .max(LIMITS.message.max, formatMessage(validation.messageMax, { max: LIMITS.message.max })),
      terms: z.boolean().refine((value) => value, { message: validation.termsRequired }),
    })
    .strict()
}

type FormSchema = ReturnType<typeof createSchema>
type FormValues = Infer<FormSchema>

export function ContactForm({
  copy,
  fields,
  placeholders,
  actions,
  legalLabels,
  validation,
  submitEndpoint = '/api/contact',
  locale,
}: ContactFormProps) {
  const termsId = useId()
  const companyId = useId()
  const nameId = useId()
  const emailId = useId()
  const phoneId = useId()
  const messageId = useId()
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaError, setCaptchaError] = useState<string | null>(null)
  const [captchaResetKey, setCaptchaResetKey] = useState(0)
  const { openLegal } = useLegalDialog()
  const handleLegalClick = (event: MouseEvent<HTMLButtonElement>, key: 'legal-terms' | 'legal-privacy') => {
    event.preventDefault()
    event.stopPropagation()
    openLegal(key)
  }

  const schema = useMemo(() => createSchema(validation), [validation])

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      company: '',
      name: '',
      email: '',
      phone: '',
      message: '',
      terms: false,
    },
  })
  const onSubmit = handleSubmit(async (values) => {
    if (recaptchaSiteKey && !captchaToken) {
      setCaptchaError(validation.captchaRequired)
      return
    }
    setStatus('submitting')
    try {
      const response = await fetch(submitEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values,
          locale,
          token: recaptchaSiteKey ? captchaToken : undefined,
        }),
      })

      const contentType = response.headers.get('content-type') ?? ''
      const isJson = contentType.includes('application/json')
      const data = isJson ? await response.json().catch(() => null) : await response.text()

      if (!response.ok) {
        const message =
          typeof data === 'string' ? data : typeof data?.message === 'string' ? data.message : validation.submitFailed
        throw new Error(message)
      }

      // The backend returns the shared dedup eventId, `contact.<sha256(email)>` — keyed on the
      // person, not on a wall-clock second. It used to be the latter, which collapsed two visitors
      // who wrote in the same second into one conversion at Meta.
      const eventId = typeof data === 'object' && typeof data?.eventId === 'string' ? data.eventId : undefined
      // The identity the server-side conversion carries. Sent with the pixel so
      // both halves of the deduplicated pair name the same person.
      const externalId = typeof data === 'object' && typeof data?.externalId === 'string' ? data.externalId : undefined

      // Contact details are PII, so keep them off the event and identify the
      // person instead; the event itself only carries non-PII context.
      // Split into the two fields the CDP destinations actually read — `first_name` and
      // `last_name` — rather than one `name` nothing consumes. The Manager writes the same two
      // from its side, and `splitFullName()` splits on the first space so a tussenvoegsel stays
      // with the surname: "Jan van der Berg" is Jan, van der Berg.
      const { firstName, lastName } = splitFullName(values.name)
      identifyByEmail(values.email, {
        first_name: firstName,
        last_name: lastName,
        phone: phoneForPerson(values.phone),
      })
      analytics.capture('contact_form_submitted', {
        locale,
        has_phone: Boolean(values.phone),
        conversion_id: eventId,
      })

      // Fire the browser ad pixels; they dedup against the server-side CAPI via this eventId.
      firePixelConversion('contact', {
        eventId,
        externalId,
        email: values.email,
        phone: values.phone,
        firstName,
        lastName,
      })

      setStatus('success')
      reset()
    } catch (error) {
      analytics.captureException(error)
      const message = error instanceof Error ? error.message : validation.submitFailed
      setStatus('error')
      setError('root', { message })
    } finally {
      setCaptchaToken(null)
      setCaptchaResetKey((value) => value + 1)
    }
  })

  if (status === 'success') {
    return (
      // `role="status"` because this replaces the whole form: without it a screen-reader user is
      // left on a control that no longer exists with no announcement that the message was sent.
      <div role="status" className="space-y-6 py-10 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <CheckIcon />
        </div>
        <div>
          <h4 className="text-2xl font-semibold text-mist-950 dark:text-white">{copy.success.title}</h4>
          <p className="mt-3 text-sm text-mist-500 dark:text-white/70">{copy.success.body}</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <form
        className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2"
        action="#"
        method="post"
        onSubmit={onSubmit}
        noValidate
      >
        {errors.root?.message && <FormErrorBanner className="sm:col-span-2">{errors.root.message}</FormErrorBanner>}

        <Field className="sm:col-span-1">
          <Label htmlFor={companyId} className="text-sm/6 font-medium text-mist-900 dark:text-mist-100" required>
            {fields.company}
          </Label>
          <Input
            id={companyId}
            type="text"
            {...register('company')}
            placeholder={placeholders.company}
            invalid={Boolean(errors.company)}
            aria-invalid={Boolean(errors.company)}
            aria-describedby={errors.company ? `${companyId}-error` : undefined}
            disabled={isSubmitting}
          />
          {errors.company?.message && <ErrorMessage id={`${companyId}-error`}>{errors.company.message}</ErrorMessage>}
        </Field>

        <Field className="sm:col-span-1">
          <Label htmlFor={nameId} className="text-sm/6 font-medium text-mist-900 dark:text-mist-100" required>
            {fields.name}
          </Label>
          <Input
            id={nameId}
            type="text"
            {...register('name')}
            placeholder={placeholders.name}
            invalid={Boolean(errors.name)}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? `${nameId}-error` : undefined}
            disabled={isSubmitting}
          />
          {errors.name?.message && <ErrorMessage id={`${nameId}-error`}>{errors.name.message}</ErrorMessage>}
        </Field>

        <Field className="sm:col-span-1">
          <Label htmlFor={emailId} className="text-sm/6 font-medium text-mist-900 dark:text-mist-100" required>
            {fields.email}
          </Label>
          <Input
            id={emailId}
            type="email"
            {...register('email')}
            placeholder={placeholders.email}
            invalid={Boolean(errors.email)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? `${emailId}-error` : undefined}
            disabled={isSubmitting}
          />
          {errors.email?.message && <ErrorMessage id={`${emailId}-error`}>{errors.email.message}</ErrorMessage>}
        </Field>

        <Field className="sm:col-span-1">
          <Label htmlFor={phoneId} className="text-sm/6 font-medium text-mist-900 dark:text-mist-100">
            {fields.phone}
          </Label>
          <Input
            id={phoneId}
            type="tel"
            {...register('phone')}
            placeholder={placeholders.phone}
            invalid={Boolean(errors.phone)}
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={errors.phone ? `${phoneId}-error` : undefined}
            disabled={isSubmitting}
          />
          {errors.phone?.message && <ErrorMessage id={`${phoneId}-error`}>{errors.phone.message}</ErrorMessage>}
        </Field>

        <Field className="sm:col-span-2">
          <Label htmlFor={messageId} className="text-sm/6 font-medium text-mist-900 dark:text-mist-100" required>
            {fields.message}
          </Label>
          <Textarea
            id={messageId}
            rows={5}
            {...register('message')}
            placeholder={placeholders.message}
            invalid={Boolean(errors.message)}
            aria-invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? `${messageId}-error` : undefined}
            disabled={isSubmitting}
          />
          {errors.message?.message && <ErrorMessage id={`${messageId}-error`}>{errors.message.message}</ErrorMessage>}
        </Field>

        <CheckboxField className="sm:col-span-2">
          <Controller
            name="terms"
            control={control}
            render={({ field }) => (
              <Checkbox
                id={termsId}
                name={field.name}
                ref={field.ref}
                checked={Boolean(field.value)}
                onBlur={field.onBlur}
                onChange={(event) => field.onChange(event.target.checked)}
                disabled={isSubmitting}
                aria-invalid={Boolean(errors.terms)}
                aria-describedby={errors.terms ? `${termsId}-error` : undefined}
              />
            )}
          />
          <Label htmlFor={termsId} className="!text-sm/6 !font-normal !text-mist-700 dark:!text-mist-300" required>
            {copy.termsPrefix}{' '}
            <button
              type="button"
              className="cursor-pointer text-mist-700 underline underline-offset-4 transition hover:text-mist-950 dark:text-mist-300 dark:hover:text-white"
              onClick={(event) => handleLegalClick(event, 'legal-terms')}
            >
              {legalLabels['legal-terms']}
            </button>{' '}
            {copy.termsMiddle}{' '}
            {/* The button renders inline-block, which gives the browser a break opportunity right
                after it — letting the suffix and required asterisk wrap onto a line of their own. */}
            <span className="whitespace-nowrap">
              <button
                type="button"
                className="cursor-pointer text-mist-700 underline underline-offset-4 transition hover:text-mist-950 dark:text-mist-300 dark:hover:text-white"
                onClick={(event) => handleLegalClick(event, 'legal-privacy')}
              >
                {legalLabels['legal-privacy']}
              </button>
              {copy.termsSuffix}
            </span>
          </Label>
          {errors.terms?.message && (
            <ErrorMessage id={`${termsId}-error`} className="col-start-2 row-start-2">
              {errors.terms.message}
            </ErrorMessage>
          )}
        </CheckboxField>

        <div className="mt-2 sm:col-span-2">
          <ReCaptcha
            locale={locale}
            action="CONTACT"
            onChange={(token) => {
              setCaptchaToken(token)
              if (token) {
                setCaptchaError(null)
              }
            }}
            error={captchaError}
            resetKey={captchaResetKey}
            unavailableLabel={validation.captchaUnavailable}
          />
        </div>

        <div className="flex flex-col items-start gap-3 pt-2 sm:col-span-2">
          <Button type="submit" size="lg" color="dark/zinc" disabled={isSubmitting}>
            {isSubmitting ? actions.submitLoading : actions.submit}
          </Button>
          <p className="text-xs/6 text-mist-500 dark:text-mist-400">{copy.privacyNote}</p>
        </div>
      </form>
    </>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true" fill="none" stroke="currentColor">
      <path d="M5 13l4 4L19 7" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
